import React, { useState, useEffect } from 'react';
import { FiBell, FiCheck, FiClock, FiUsers, FiTruck, FiCreditCard, FiRefreshCw, FiCheckCircle, FiActivity } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { socket } from '../../socket/socket';
import { useAuth } from '../../context/AuthContext';

const SuperAdminNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ 
    total: 0, 
    vendors: 0, 
    delivery: 0, 
    plans: 0 
  });

  useEffect(() => {
    fetchNotifications();

    // Socket room join is handled globally in Sidebar.js
    if (user?.role === 'superadmin') {
      // Listen for new notifications and refresh list
      socket.on('vendor:newRegistration', (payload) => {
        toast.success('New Vendor Registration');
        fetchNotifications();
      });

      socket.on('deliveryRequest:new', (payload) => {
        toast.success('New Delivery Request');
        fetchNotifications();
      });

      socket.on('planRequest:new', (payload) => {
        toast.success('New Plan Request');
        fetchNotifications();
      });

      socket.on('vendor:documentsUpdated', (payload) => {
        toast.success('Vendor Documents Updated');
        fetchNotifications();
      });

      return () => {
        socket.off('vendor:newRegistration');
        socket.off('deliveryRequest:new');
        socket.off('planRequest:new');
        socket.off('vendor:documentsUpdated');
      };
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/superadmin/notifications?limit=100');
      if (response.data.success) {
        const notifs = response.data.data;
        setNotifications(notifs);
        calculateStats(notifs);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    calculateStats([notification, ...notifications]);
    toast.success(notification.title);
  };

  const calculateStats = (notifs) => {
    setStats({
      total: notifs.length,
      vendors: notifs.filter(n => n.type === 'vendor_registration').length,
      delivery: notifs.filter(n => n.type === 'delivery_request').length,
      plans: notifs.filter(n => n.type === 'plan_request').length
    });
  };

  const markAsRead = async (id) => {
    try {
      await api.patch(`/superadmin/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => (n._id === id ? { ...n, isRead: true } : n))
      );
      calculateStats(notifications.map(n => (n._id === id ? { ...n, isRead: true } : n)));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/superadmin/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      calculateStats(notifications.map(n => ({ ...n, isRead: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const formatTimeAgo = (date) => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) > 1 ? 's' : ''} ago`;
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'vendor_registration': return <FiUsers />;
      case 'delivery_request': return <FiTruck />;
      case 'plan_request': return <FiCreditCard />;
      default: return <FiBell />;
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'vendor_registration': return 'sa-notif-vendor';
      case 'delivery_request': return 'sa-notif-delivery';
      case 'plan_request': return 'sa-notif-plan';
      default: return 'sa-notif-default';
    }
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === 'vendors') return n.type === 'vendor_registration';
    if (filter === 'delivery') return n.type === 'delivery_request';
    if (filter === 'plans') return n.type === 'plan_request';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading Notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Enhanced Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FiBell className="text-white text-2xl" />
                </div>
                Super Admin Notifications
              </h1>
              <p className="text-gray-600 mt-2">Manage and monitor system notifications in real-time</p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 rounded-full text-sm font-semibold flex items-center gap-2">
                <FiActivity className="text-indigo-600" />
                {notifications.length} Total
              </div>
              <button
                onClick={fetchNotifications}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FiRefreshCw className="text-sm" />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
          <div className="flex flex-wrap gap-3 mb-4">
            <button 
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                filter === 'all' 
                  ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('all')}
            >
              <FiActivity className="text-sm" />
              All ({stats.total})
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                filter === 'vendors' 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('vendors')}
            >
              <FiUsers className="text-sm" />
              Vendors ({stats.vendors})
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                filter === 'delivery' 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('delivery')}
            >
              <FiTruck className="text-sm" />
              Delivery ({stats.delivery})
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                filter === 'plans' 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('plans')}
            >
              <FiCreditCard className="text-sm" />
              Plans ({stats.plans})
            </button>
          </div>
          
          {notifications.some(n => !n.isRead) && (
            <button 
              onClick={markAllAsRead}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <FiCheckCircle className="text-sm" />
              <span>Mark all as read</span>
            </button>
          )}
        </div>

      {/* Enhanced List Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {filter === 'all' && `All Notifications (${filteredNotifications.length})`}
              {filter === 'vendors' && `Vendor Registrations (${filteredNotifications.length})`}
              {filter === 'delivery' && `Delivery Requests (${filteredNotifications.length})`}
              {filter === 'plans' && `Plan Requests (${filteredNotifications.length})`}
            </h2>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <FiActivity className="text-gray-400" />
              <span>Real-time updates</span>
            </div>
          </div>

          {filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-16 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <FiBell className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No notifications yet</h3>
              <p className="text-gray-500 mb-6">New notifications will appear here when vendors register or make requests.</p>
              <button
                onClick={() => setFilter('all')}
                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                View All Notifications
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((n) => (
                <div
                  key={n._id}
                  className={`bg-white rounded-xl shadow-md border border-gray-100 p-6 transition-all duration-300 hover:shadow-lg hover:border-indigo-300 ${
                    !n.isRead ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Notification Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      n.type === 'vendor_registration' 
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                        : n.type === 'delivery_request' 
                          ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                          : n.type === 'plan_request' 
                            ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                            : 'bg-gradient-to-br from-gray-500 to-gray-600'
                    }`}>
                      <div className="text-white">
                        {getNotificationIcon(n.type)}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          n.type === 'vendor_registration' 
                            ? 'bg-blue-100 text-blue-800' 
                            : n.type === 'delivery_request' 
                              ? 'bg-amber-100 text-amber-800'
                              : n.type === 'plan_request' 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}>
                          {n.type.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <FiClock size={12} />
                          {formatTimeAgo(n.createdAt)}
                        </span>
                      </div>

                      <h4 className="text-lg font-bold text-gray-900 mb-1">{n.title}</h4>
                      <p className="text-gray-600 mb-3">{n.message}</p>

                      {n.data && (
                        <div className="flex flex-wrap gap-2">
                          {n.data.name && (
                            <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-700 flex items-center gap-1">
                              <FiUsers size={12} />
                              {n.data.name}
                            </span>
                          )}
                          {n.data.restaurantName && (
                            <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-700">
                              {n.data.restaurantName}
                            </span>
                          )}
                          {n.data.email && (
                            <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-700">
                              {n.data.email}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center">
                      {!n.isRead ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(n._id);
                          }}
                          className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg"
                          title="Mark as read"
                        >
                          <FiCheck size={16} />
                        </button>
                      ) : (
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <FiCheckCircle className="text-green-500" size={16} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminNotifications;
